/**
 * Telemetry tuning — every threshold in one place.
 *
 * Overridable per environment via `.env.*`. Note CRA inlines env vars at BUILD
 * time, so these are per-deployment knobs, not runtime settings.
 */

/** Parse a numeric env override, ignoring anything invalid. */
const envMs = (name: string, fallback: number): number => {
  const parsed = Number(process.env[name]);
  // Reject NaN/negative so a typo in .env cannot silently disable tracking.
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
};

/**
 * Idle gate. Engaged time stops accruing after this long with no interaction,
 * and resumes on the next one — within the SAME session.
 *
 * Ticket: "No time tracking if idle for more than 10 secs … on mouse move start
 * the tracking again for the same session."
 */
export const TELEMETRY_IDLE_GATE_MS = envMs("REACT_APP_TELEMETRY_IDLE_GATE_MS", 10_000);

/**
 * Minimum hover before a KPI counts as looked at. Shorter hovers are the pointer
 * passing over on its way elsewhere.
 */
export const TELEMETRY_KPI_HOVER_MIN_MS = envMs("REACT_APP_TELEMETRY_KPI_HOVER_MIN_MS", 3_000);

/**
 * Heartbeat is idle-only: sent only after this much silence with nothing else
 * queued, so a user who is clicking around sends none at all.
 */
export const TELEMETRY_HEARTBEAT_IDLE_MS = envMs("REACT_APP_TELEMETRY_HEARTBEAT_IDLE_MS", 300_000);

/** Flush once this many events are buffered. */
export const TELEMETRY_FLUSH_MAX_EVENTS = envMs("REACT_APP_TELEMETRY_FLUSH_MAX_EVENTS", 20);

/** Flush at least this often while the tab is visible. */
export const TELEMETRY_FLUSH_INTERVAL_MS = envMs("REACT_APP_TELEMETRY_FLUSH_INTERVAL_MS", 10_000);

/** Hard cap per request. The backend rejects larger batches (422). */
export const TELEMETRY_MAX_BATCH = 50;

/**
 * Ceiling on the outbound buffer.
 *
 * Failed batches are put back so an ordinary token-refresh window does not lose
 * data, which means a sustained outage would otherwise grow this without limit.
 * Beyond this the OLDEST events are dropped — recent activity is the more useful
 * half, and losing telemetry is acceptable where blocking the app is not.
 */
export const TELEMETRY_BUFFER_MAX = 200;

/**
 * mousemove fires 60–120×/sec. Recording activity more often than this buys no
 * precision against a 10s gate and would burn CPU on every pointer move.
 */
export const TELEMETRY_ACTIVITY_THROTTLE_MS = 1_000;

/**
 * What counts as interaction. `mousemove` is included per the ticket — "idle
 * means no movement even mouse". Removing it here is the one-line change to
 * make the gate measure deliberate interaction instead of presence.
 */
export const TELEMETRY_ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "scroll",
  "wheel",
  "touchstart",
] as const;
